import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  TextField,
  Stack,
  Paper,
  Pagination,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import ProductService from '../../services/product.service';
import { usePagination } from '../../hooks/usePagination';

const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const { pagination, setPagination, handlePageChange, handlePageSizeChange } = usePagination(12);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchProducts();
  }, [pagination.page, pagination.pageSize, searchTerm]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params: any = {
        populate: ['images', 'category', 'supplier'],
        'pagination[page]': pagination.page + 1, // Strapi uses 1-based indexing
        'pagination[pageSize]': pagination.pageSize,
      };

      if (searchTerm) {
        params['filters[$or][0][name][$containsi]'] = searchTerm;
        params['filters[$or][1][sku][$containsi]'] = searchTerm;
      }

      const response = await ProductService.getAll(params);
      const productsData = response.data?.data || [];
      const paginationMeta = response.data?.meta?.pagination || {};
      
      setProducts(productsData);
      setPagination({
        total: paginationMeta.total || 0,
        pageCount: paginationMeta.pageCount || 0,
      });
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      setPagination({ total: 0, pageCount: 0 });
    } finally {
      setLoading(false);
    }
  };

  return (
      <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Products</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/products/create')}
        >
          Add Product
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          label="Search products..."
          variant="outlined"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            handlePageChange(0);
          }}
        />
      </Paper>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Stack direction="row" flexWrap="wrap" gap={3}>
            {products.map((product) => (
            <Box key={product.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.33% - 16px)', lg: 'calc(25% - 18px)' } }}>
              <Card
                sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
                onClick={() => navigate(`/products/${product.id}`)}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={
                    product.attributes?.images?.data?.[0]?.attributes?.url
                      ? `${(process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '')}${product.attributes.images.data[0].attributes.url}`
                      : 'https://via.placeholder.com/400x300?text=No+Image'
                  }
                  alt={product.attributes?.name}
                  sx={{ objectFit: 'cover', bgcolor: 'grey.100' }}
                />
                <CardContent>
                  <Typography variant="h6" gutterBottom noWrap>
                    {product.attributes?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    SKU: {product.attributes?.sku}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                    {product.attributes?.currency || 'OMR'} {product.attributes?.price?.toFixed(2)}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip
                      label={product.attributes?.isActive ? 'Active' : 'Inactive'}
                      color={product.attributes?.isActive ? 'success' : 'default'}
                      size="small"
                    />
                    {product.attributes?.category?.data && (
                      <Chip
                        label={product.attributes.category.data.attributes?.name}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Stack>
        {pagination.pageCount > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={pagination.pageCount}
              page={pagination.page + 1}
              onChange={(_, page) => handlePageChange(page - 1)}
              color="primary"
              size="large"
            />
          </Box>
        )}
        </>
      )}

      {!loading && products.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No products found
          </Typography>
        </Paper>
      )}
      </Box>
  );
};

export default ProductList;

